/**
 * Base Route Definitions — Shared across all unified templates.
 *
 * This file defines the common route tree using page components sourced
 * from routeAdapters.js. Template-specific overrides are injected in
 * routing/index.js via the template extension mechanism, not here.
 *
 * Rule: Do NOT add template-specific `if (skin === ...)` branches here.
 * Use templateExtensions.componentOverrides or templateExtensions.additions instead.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';

import {
    DashboardPage,
    ProfilePage,
    PaymentPage,
    PayoutPage,
    PayoutItemPage,
    OperationsHistoryPage,
    CurrencyRatioPage,
    GoalsPage,
    OfferPage,
    StatisticsPage,
    StatisticsByDayPage,
    StatisticsByUTMPage,
    StatisticsByCreativePage,
    StatisticsByRefererPage,
    StatisticsByReferralPage,
    StatisticsByReactivationPage,
    StatisticsByCountryPage,
    ListPostbackPage,
    CreatePostbackPage,
    HistoryPostbackPage,
    EditPostbackPage,
    ConversionPage,
    ConversionItemPage,
    FinanceReportPage,
    InformationPage,
    TimezonesPage,
} from '../adapters/routeAdapters';

const StatisticsRootRedirect = () => <Navigate to="/statistics/report" replace />;
const FinanceRootRedirect = () => <Navigate to="/office/finances/payout" replace />;
const PostbackRootRedirect = () => <Navigate to="/postback/list" replace />;

/**
 * Each entry:
 *   key       — stable route identifier used by template extensions to override
 *   path      — URL path (omit for the index route)
 *   index     — true for the default child route
 *   component — React component rendered at this route
 */
const baseRoutes = [
    { key: 'dashboard',             index: true,                                                   component: DashboardPage },
    { key: 'profile',               path: '/office/settings/profile',                              component: ProfilePage },
    { key: 'payment',               path: '/office/settings/payment',                              component: PaymentPage },
    { key: 'information',           path: '/office/settings/information',                          component: InformationPage },
    { key: 'timezones',             path: '/timezones',                                            component: TimezonesPage },
    { key: 'financeRoot',           path: '/office/finances',                                      component: FinanceRootRedirect },
    { key: 'payout',                path: '/office/finances/payout',                               component: PayoutPage },
    { key: 'payoutItem',            path: '/office/finances/payout/:id',                           component: PayoutItemPage },
    { key: 'historyFinance',        path: '/office/finances/history',                              component: OperationsHistoryPage },
    { key: 'financeCurrency',       path: '/office/finances/currency',                             component: CurrencyRatioPage },
    { key: 'financeReport',         path: '/office/finances/report',                               component: FinanceReportPage },
    { key: 'goals',                 path: '/offers/goals',                                         component: GoalsPage },
    { key: 'offer',                 path: '/offers/offer/:id',                                     component: OfferPage },
    { key: 'statisticsRoot',        path: '/statistics',                                            component: StatisticsRootRedirect },
    { key: 'statistics',            path: '/statistics/report',                                    component: StatisticsPage },
    { key: 'reportByDay',           path: '/statistics/report-by-day',                             component: StatisticsByDayPage },
    { key: 'reportByCountry',       path: '/statistics/report-by-country',                         component: StatisticsByCountryPage },
    { key: 'reportByUTM',           path: '/statistics/report-by-utm',                             component: StatisticsByUTMPage },
    { key: 'reportByCreative',      path: '/statistics/report-by-creative',                        component: StatisticsByCreativePage },
    { key: 'reportByReferer',       path: '/statistics/report-by-referer',                         component: StatisticsByRefererPage },
    { key: 'reportByReactivation',  path: '/statistics/report-by-reactivation',                    component: StatisticsByReactivationPage },
    { key: 'reportByReferral',      path: '/statistics/report-by-referral',                        component: StatisticsByReferralPage },
    { key: 'postbackRoot',          path: '/postback',                                              component: PostbackRootRedirect },
    { key: 'instruments',           path: '/postback/list',                                        component: ListPostbackPage },
    { key: 'createPostback',        path: '/postback/create',                                      component: CreatePostbackPage },
    { key: 'historyPostback',       path: '/postback/history',                                     component: HistoryPostbackPage },
    { key: 'editPostback',          path: '/postback/edit/:id',                                    component: EditPostbackPage },
    { key: 'conversions',           path: '/statistics/conversions',                               component: ConversionPage },
    { key: 'conversion',            path: '/statistics/conversion/:id',                            component: ConversionItemPage },
];

export default baseRoutes;
